import React, { useState, useEffect, forwardRef } from "react";
import "react-datepicker/dist/react-datepicker.css"; // Import CSS for styling
import "./datePicker.scss";
import clsx from "clsx";

const CustomDatePicker = ({
  inputName = "",
  minDate = "",
  selected = "",
  placeholderText = "",
  additionalClass = "",
  setSelectedOptions,
  isClearable = true,
  setIsUserInteraction = () => {},
  onDateSelected = () => {},
}) => {
  const [DatePicker, setDatePicker] = useState(null);

  // Dynamic import for DatePicker
  useEffect(() => {
    import("react-datepicker").then((module) => {
      setDatePicker(() => module.default);
    });
  }, []);

  if (!DatePicker) return <p>Loading date picker...</p>;

  // Helper function to format date to MM-DD-YYYY
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);

    // Check if the date is valid
    if (isNaN(d.getTime())) {
      console.error("Invalid date value:", date);
      return "";
    }

    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper function to safely parse date
  const parseDate = (dateString) => {
    if (!dateString) return null;

    // If it's already a Date object, return it
    if (dateString instanceof Date) return dateString;

    // Try parsing the date
    const parsedDate = new Date(dateString);

    // Check if the date is valid
    if (isNaN(parsedDate.getTime())) {
      console.error("Invalid date value:", dateString);
      return null;
    }

    return parsedDate;
  };

  // Use forwardRef to allow DatePicker to set a ref on this component
  const ReadonlyInput = forwardRef(({ value, onClick }, ref) => (
    <div
      style={{ position: "relative", display: "inline-block", width: "100%" }}
    >
      <input
        className={clsx(!additionalClass && "datePickerField", additionalClass)}
        name={inputName}
        onClick={onClick}
        value={value}
        placeholder={placeholderText}
        readOnly
        ref={ref} // Pass the ref to the input element
        style={{ width: "100%", paddingRight: "30px" }}
      />
      <span
        style={{
          position: "absolute",
          right: "10px",
          top: "48%",
          transform: "translateY(-52%)",
          pointerEvents: "none", // Ensures icon click doesn’t trigger input focus
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.66699 1.66797V4.16797"
            stroke="#403D3A"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.333 1.66797V4.16797"
            stroke="#403D3A"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2.91699 7.57422H17.0837"
            stroke="#403D3A"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.5 7.08464V14.168C17.5 16.668 16.25 18.3346 13.3333 18.3346H6.66667C3.75 18.3346 2.5 16.668 2.5 14.168V7.08464C2.5 4.58464 3.75 2.91797 6.66667 2.91797H13.3333C16.25 2.91797 17.5 4.58464 17.5 7.08464Z"
            stroke="#403D3A"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.0791 11.4167H13.0866"
            stroke="#403D3A"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.0791 13.9167H13.0866"
            stroke="#403D3A"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.99607 11.4167H10.0036"
            stroke="#403D3A"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.99607 13.9167H10.0036"
            stroke="#403D3A"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.91209 11.4167H6.91957"
            stroke="#403D3A"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.91162 13.916H6.91911"
            stroke="#403D3A"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  ));

  // Helper function to get a valid minimum date
  const getValidMinDate = () => {
    // Handle empty or undefined minDate
    if (!minDate) {
      return new Date(); // Default to current date
    }

    const parsedMinDate = parseDate(minDate);
    
    // More explicit null checking
    if (parsedMinDate !== null && !isNaN(parsedMinDate.getTime())) {
      return parsedMinDate;
    }
    
    // If no valid minDate provided, use current date
    return new Date();
  };

  return (
    <div className="datePicker">
      {inputName && <div className="datePickerLabel">{inputName}</div>}
      <DatePicker
        showIcon
        inputName="Move-In Date"
        isClearable={isClearable}
        selected={parseDate(selected)}
        customInput={<ReadonlyInput />}
        minDate={getValidMinDate()}
        onChange={(date) => {
          if (setIsUserInteraction) {
            setIsUserInteraction(true);
          }
          if (!date) {
            setSelectedOptions((prev) => ({
              ...prev,
              moveInStartDate: "",
            }));
            return;
          }

          onDateSelected();
          const formattedDate = formatDate(date);
          setSelectedOptions((prev) => ({
            ...prev,
            moveInStartDate: formattedDate,
          }));
        }}
        dateFormat="MM/dd/yyyy"
        placeholderText={"MM/DD/YYYY"}
        calendarClassName="custom-calendar"
      />
    </div>
  );
};

export default CustomDatePicker;
